import { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from "recharts";

const NAVY = "#0C2340";
const GOLD = "#C5A25D";
const BEIGE = "#F7F4ED";
const SLATE = "#5B6770";
const GREEN = "#2E7D32";
const AMBER = "#F59E0B";
const RED = "#DC2626";

const Section = ({ title, children, step }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ background: NAVY, padding: "10px 18px", display: "flex", alignItems: "center", gap: 12, borderRadius: 6, marginBottom: 14 }}>
      <div style={{ background: GOLD, color: NAVY, borderRadius: "50%", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>{step}</div>
      <span style={{ color: GOLD, fontWeight: 700, fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase" }}>{title}</span>
    </div>
    <div style={{ padding: "0 4px" }}>{children}</div>
  </div>
);

const Row = ({ children, cols = 2 }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12, marginBottom: 10 }}>
    {children}
  </div>
);

const Field = ({ label, help, value, onChange, prefix, suffix, type = "number", min = 0, max }) => (
  <div>
    <label style={{ fontSize: 11, fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 3 }}>{label}</label>
    {help && <div style={{ fontSize: 10, color: SLATE, marginBottom: 4, fontStyle: "italic" }}>{help}</div>}
    <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
      {prefix && <span style={{ background: NAVY, color: GOLD, padding: "6px 8px", fontSize: 13, borderRadius: "4px 0 0 4px", fontWeight: 600 }}>{prefix}</span>}
      <input
        type={type === "number" ? "number" : "text"}
        value={value}
        min={min}
        max={max}
        onChange={e => onChange(type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value)}
        style={{
          flex: 1, padding: "6px 10px", border: `1.5px solid ${GOLD}`,
          borderLeft: prefix ? "none" : `1.5px solid ${GOLD}`,
          borderRight: suffix ? "none" : `1.5px solid ${GOLD}`,
          borderRadius: prefix ? (suffix ? 0 : "0 4px 4px 0") : (suffix ? "4px 0 0 4px" : 4),
          fontSize: 14, fontWeight: 600, color: NAVY, background: BEIGE, outline: "none", pointerEvents: "auto"
        }}
      />
      {suffix && <span style={{ background: SLATE, color: "#fff", padding: "6px 8px", fontSize: 13, borderRadius: "0 4px 4px 0", fontWeight: 600 }}>{suffix}</span>}
    </div>
  </div>
);

const Select = ({ label, help, value, onChange, options }) => (
  <div>
    <label style={{ fontSize: 11, fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 3 }}>{label}</label>
    {help && <div style={{ fontSize: 10, color: SLATE, marginBottom: 4, fontStyle: "italic" }}>{help}</div>}
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{ width: "100%", padding: "6px 10px", border: `1.5px solid ${GOLD}`, borderRadius: 4, fontSize: 13, fontWeight: 600, color: NAVY, background: BEIGE, outline: "none", pointerEvents: "auto" }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const MetricCard = ({ label, value, sub, color = NAVY, highlight }) => (
  <div style={{
    background: highlight ? NAVY : "#fff", border: `2px solid ${highlight ? GOLD : "#E5E0D8"}`,
    borderRadius: 8, padding: "14px 16px", textAlign: "center"
  }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: highlight ? GOLD : SLATE, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 800, color: highlight ? GOLD : color, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: highlight ? "#fff" : SLATE, marginTop: 4 }}>{sub}</div>}
  </div>
);

const ScenarioCard = ({ label, members, delta, revenue, color, bg, icon }) => (
  <div style={{ flex: 1, background: bg, border: `2px solid ${color}`, borderRadius: 10, padding: "16px 14px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
    </div>
    <div style={{ fontSize: 32, fontWeight: 900, color, marginBottom: 2 }}>
      {delta >= 0 ? "+" : ""}{delta}
      <span style={{ fontSize: 14, fontWeight: 600 }}> members</span>
    </div>
    <div style={{ fontSize: 12, color: SLATE, marginBottom: 6 }}>{members} total active</div>
    <div style={{ fontSize: 13, fontWeight: 700, color }}>+${revenue.toLocaleString()} / yr</div>
    <div style={{ fontSize: 10, color: SLATE }}>estimated new dues revenue</div>
  </div>
);

const Tooltip2 = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: NAVY, border: `1px solid ${GOLD}`, borderRadius: 6, padding: "10px 14px" }}>
      <p style={{ color: GOLD, fontWeight: 700, marginBottom: 4, fontSize: 12 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: "#fff", fontSize: 12, margin: "2px 0" }}>
          <span style={{ color: p.color }}>■</span> {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function AshGroCalculator() {
  // === INPUTS ===
  const [clubName, setClubName] = useState("");
  const [currentMembers, setCurrentMembers] = useState(280);
  const [capacity, setCapacity] = useState(400);
  const [avgInitiation, setAvgInitiation] = useState(8000);
  const [avgAnnualDues, setAvgAnnualDues] = useState(4200);
  const [avgFBMin, setAvgFBMin] = useState(150);
  const [attritionRate, setAttritionRate] = useState(8);
  const [monthlyLeads, setMonthlyLeads] = useState(12);
  const [tourConvRate, setTourConvRate] = useState(40);
  const [closeRate, setCloseRate] = useState(28);
  const [avgTimeToClose, setAvgTimeToClose] = useState(45);
  const [referralPct, setReferralPct] = useState(35);
  const [staffLevel, setStaffLevel] = useState("part");
  const [marketType, setMarketType] = useState("growing");
  const [competitorCount, setCompetiorsCount] = useState(3);
  const [pricingPos, setPricingPos] = useState("at");
  const [memberNPS, setMemberNPS] = useState(35);
  const [onboardingScore, setOnboardingScore] = useState(2);
  // eslint-disable-next-line no-unused-vars
  const [amenityScore, setAmenityScore] = useState(3);
  const [digitalScore, setDigitalScore] = useState(2);
  const [salesTraining, setSalesTraining] = useState("none");
  const [services, setServices] = useState({ crm: true, ai: true, staffing: false, training: true, consulting: false, remote: false });

  // === MODEL ===
  const calc = useMemo(() => {
    const annualLeads = monthlyLeads * 12;
    const toursPerYear = annualLeads * (tourConvRate / 100);
    const currentNewMembers = Math.round(toursPerYear * (closeRate / 100));
    const annualAttrition = Math.round(currentMembers * (attritionRate / 100));
    // eslint-disable-next-line no-unused-vars
    const netBaseline = currentNewMembers - annualAttrition;

    // Market multiplier
    const marketMult = { growing: 1.2, suburban: 1.0, urban: 0.95, resort: 1.1, rural: 0.85, declining: 0.7 }[marketType] || 1.0;

    // Pricing multiplier
    const pricingMult = { below: 1.15, slightly_below: 1.1, at: 1.0, slightly_above: 0.95, premium: 0.9 }[pricingPos] || 1.0;

    // Staff multiplier
    const staffMult = { none: 0.7, part: 0.85, full: 1.0, dedicated: 1.15 }[staffLevel] || 1.0;

    // Training multiplier
    const trainingMult = { none: 0.8, outdated: 0.9, recent: 1.0, ongoing: 1.15 }[salesTraining] || 1.0;

    // NPS/referral impact on attrition reduction
    const referralBonus = referralPct > 40 ? 1.1 : referralPct > 25 ? 1.05 : 1.0;

    // Services selected impact
    const serviceBoosts = {
      crm: { leadMult: 1.1, closeMult: 1.08, attritionReduction: 0.08 },
      ai: { leadMult: 1.25, closeMult: 1.05, attritionReduction: 0.04 },
      staffing: { leadMult: 1.1, closeMult: 1.25, attritionReduction: 0.05 },
      training: { leadMult: 1.0, closeMult: 1.18, attritionReduction: 0.05 },
      consulting: { leadMult: 1.05, closeMult: 1.1, attritionReduction: 0.06 },
      remote: { leadMult: 1.15, closeMult: 1.12, attritionReduction: 0.06 },
    };

    let baseLeadMult = 1, baseCloseMult = 1, baseAttritionRed = 0;
    Object.entries(services).forEach(([k, v]) => {
      if (v && serviceBoosts[k]) {
        baseLeadMult *= serviceBoosts[k].leadMult;
        baseCloseMult *= serviceBoosts[k].closeMult;
        baseAttritionRed += serviceBoosts[k].attritionReduction;
      }
    });

    const openSlots = Math.max(0, capacity - currentMembers);

    // FLOOR (status quo, no change)
    const floorNew = currentNewMembers;
    const floorAttrition = annualAttrition;
    const floorNet = floorNew - floorAttrition;
    const floorTotal = Math.min(capacity, currentMembers + floorNet);

    // BASE (AshGro services + moderate improvement)
    const baseLeadMult2 = baseLeadMult * marketMult * pricingMult * referralBonus;
    const baseCloseMult2 = baseCloseMult * staffMult * trainingMult;
    const baseNewLeads = annualLeads * baseLeadMult2;
    const baseTours = baseNewLeads * (tourConvRate / 100) * 1.15; // better inquiry-to-tour
    const baseNewMembers = Math.round(Math.min(baseTours * (closeRate / 100) * baseCloseMult2, openSlots + floorAttrition));
    const baseAttritionAmt = Math.round(annualAttrition * (1 - Math.min(baseAttritionRed, 0.3)));
    const baseNet = baseNewMembers - baseAttritionAmt;
    const baseTotal = Math.min(capacity, currentMembers + baseNet);

    // UPSIDE (aggressive, all AshGro, optimal conditions)
    const upsideLeadMult = baseLeadMult2 * 1.2;
    const upsideCloseMult = baseCloseMult2 * 1.12;
    const upsideNewLeads = annualLeads * upsideLeadMult;
    const upsideTours = upsideNewLeads * (tourConvRate / 100) * 1.25;
    const upsideNewMembers = Math.round(Math.min(upsideTours * (closeRate / 100) * upsideCloseMult, openSlots + floorAttrition + 20));
    const upsideAttritionAmt = Math.round(annualAttrition * (1 - Math.min(baseAttritionRed * 1.3, 0.4)));
    const upsideNet = upsideNewMembers - upsideAttritionAmt;
    const upsideTotal = Math.min(capacity, currentMembers + upsideNet);

    // Revenue calc (avg initiation amortized + dues + F&B)
    const avgRevPerMember = avgAnnualDues + (avgFBMin * 12);
    const floorRev = Math.max(0, floorNet) * avgRevPerMember + (floorNew * avgInitiation * 0.15);
    const baseRev = Math.max(0, baseNet) * avgRevPerMember + (baseNewMembers * avgInitiation * 0.15);
    const upsideRev = Math.max(0, upsideNet) * avgRevPerMember + (upsideNewMembers * avgInitiation * 0.15);

    // 36-month projections
    const proj36 = [
      { month: "Now", floor: currentMembers, base: currentMembers, upside: currentMembers },
    ];
    let f = currentMembers, b = currentMembers, u = currentMembers;
    for (let q = 1; q <= 12; q++) {
      f = Math.min(capacity, Math.round(f + (floorNet / 12)));
      b = Math.min(capacity, Math.round(b + (baseNet / 12)));
      u = Math.min(capacity, Math.round(u + (upsideNet / 12)));
      if (q % 3 === 0) proj36.push({ month: `Mo ${q}`, floor: f, base: b, upside: u });
    }

    // Conversion funnel
    const funnel = [
      { stage: "Annual Leads", current: Math.round(annualLeads), improved: Math.round(annualLeads * baseLeadMult2) },
      { stage: "Tours Conducted", current: Math.round(toursPerYear), improved: Math.round(baseTours) },
      { stage: "New Members", current: currentNewMembers, improved: baseNewMembers },
    ];

    // Key insights
    const insights = [];
    if (closeRate < 30) insights.push({ type: "warning", text: `Close rate of ${closeRate}% is below the industry average of 35–45%. Improving this alone could yield ${Math.round(toursPerYear * 0.1)} more members annually.` });
    if (attritionRate > 10) insights.push({ type: "critical", text: `Attrition at ${attritionRate}% is elevated. Industry benchmark is 5–8%. Recovering just 3% saves ~${Math.round(currentMembers * 0.03)} members per year.` });
    if (tourConvRate < 40) insights.push({ type: "warning", text: `Inquiry-to-tour rate of ${tourConvRate}% has room to grow. Better follow-up and an AI agent can lift this to 55–65%.` });
    if (monthlyLeads < 10) insights.push({ type: "info", text: `Lead volume of ${monthlyLeads}/month is low relative to capacity. Digital marketing and AI engagement could double this.` });
    if (digitalScore <= 2) insights.push({ type: "info", text: "Weak digital presence likely suppresses lead volume significantly. Website and AI investment would show fast returns." });
    if (openSlots > capacity * 0.25) insights.push({ type: "opportunity", text: `${openSlots} open slots represent $${((openSlots * avgRevPerMember) / 1000).toFixed(0)}K in annual revenue opportunity at full capacity.` });
    const capacityPct = Math.round((currentMembers / capacity) * 100);
    if (capacityPct > 90) insights.push({ type: "info", text: `At ${capacityPct}% capacity, growth is constrained. Focus shifts to retention, attrition reduction, and waitlist management.` });

    return {
      annualLeads, toursPerYear, currentNewMembers, annualAttrition,
      floorNet, floorTotal, floorRev: Math.round(floorRev),
      baseNet, baseTotal, baseRev: Math.round(baseRev),
      upsideNet, upsideTotal, upsideRev: Math.round(upsideRev),
      proj36, funnel, insights, capacityPct, openSlots, avgRevPerMember
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembers, capacity, avgInitiation, avgAnnualDues, avgFBMin, attritionRate, monthlyLeads, tourConvRate, closeRate, referralPct, staffLevel, marketType, pricingPos, digitalScore, salesTraining, services]);

  const toggleService = (k) => setServices(prev => ({ ...prev, [k]: !prev[k] }));

  const insightColors = { warning: { bg: "#FEF3C7", border: AMBER, icon: "⚠️" }, critical: { bg: "#FEE2E2", border: RED, icon: "🔴" }, info: { bg: "#E0F2FE", border: "#0284C7", icon: "💡" }, opportunity: { bg: "#DCFCE7", border: GREEN, icon: "🎯" } };

  return (
    <div style={{ fontFamily: "'Open Sans', sans-serif", maxWidth: "100%", margin: "0 auto", background: "#F5F3EE", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ background: NAVY, padding: "20px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ color: GOLD, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 4 }}>AshGro Membership Partners</div>
          <div style={{ color: "#fff", fontSize: 20, fontWeight: 800 }}>Membership Growth Projection Tool</div>
          <div style={{ color: "#94A3B8", fontSize: 11, marginTop: 3 }}>Internal Sales Tool — Confidential</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <input
            placeholder="Club Name..."
            value={clubName}
            onChange={e => setClubName(e.target.value)}
            style={{ background: "rgba(255,255,255,0.1)", border: `1px solid ${GOLD}`, color: "#fff", padding: "6px 12px", borderRadius: 4, fontSize: 13, outline: "none", textAlign: "right" }}
          />
          <div style={{ color: GOLD, fontSize: 10, marginTop: 4 }}>Enter club name to personalize</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "460px 1fr", gap: 0, alignItems: "start" }}>
        {/* LEFT: Inputs */}
        <div style={{ background: "#fff", padding: "20px 20px", borderRight: `3px solid ${GOLD}`, height: "100%", overflowY: "auto", width: "100%", boxSizing: "border-box" }}>

          <Section step="1" title="Current Membership">
            <Row>
              <Field label="Current Members" value={currentMembers} onChange={setCurrentMembers} />
              <Field label="Total Capacity" value={capacity} onChange={setCapacity} />
            </Row>
            <Row cols={1}>
              <Field label="Avg Initiation Fee" value={avgInitiation} onChange={setAvgInitiation} prefix="$" />
              <Field label="Annual Dues (avg)" value={avgAnnualDues} onChange={setAvgAnnualDues} prefix="$" />
            </Row>
            <Field label="Monthly F&B Minimum" value={avgFBMin} onChange={setAvgFBMin} prefix="$" help="Per member per month" />
          </Section>

          <Section step="2" title="Attrition & Retention">
            <Row>
              <Field label="Annual Attrition Rate" value={attritionRate} onChange={setAttritionRate} suffix="%" min={0} max={50} help="% of members who resign" />
              <Field label="Member Referral %" value={referralPct} onChange={setReferralPct} suffix="%" help="Leads from referrals" />
            </Row>
            <Row>
              <Select label="Onboarding Quality" value={onboardingScore} onChange={v => setOnboardingScore(v)} options={[
                { value: "1", label: "1 — No formal process" },
                { value: "2", label: "2 — Informal" },
                { value: "3", label: "3 — Basic structure" },
                { value: "4", label: "4 — Good process" },
                { value: "5", label: "5 — Excellent" },
              ]} />
              <Field label="Member NPS Score" value={memberNPS} onChange={setMemberNPS} min={-100} max={100} help="-100 to 100" />
            </Row>
          </Section>

          <Section step="3" title="Sales Pipeline">
            <Row>
              <Field label="Monthly Leads" value={monthlyLeads} onChange={setMonthlyLeads} help="Avg inquiries per month" />
              <Field label="Inquiry → Tour Rate" value={tourConvRate} onChange={setTourConvRate} suffix="%" help="% that book a tour" />
            </Row>
            <Row>
              <Field label="Tour Close Rate" value={closeRate} onChange={setCloseRate} suffix="%" help="% of tours that join" />
              <Field label="Days to Close" value={avgTimeToClose} onChange={setAvgTimeToClose} help="Avg days inquiry to join" />
            </Row>
            <Select label="Membership Staffing" value={staffLevel} onChange={setStaffLevel} options={[
              { value: "none", label: "No dedicated staff" },
              { value: "part", label: "Part-time / Shared role" },
              { value: "full", label: "Full-time Membership Director" },
              { value: "dedicated", label: "Dedicated + experienced" },
            ]} />
            <div style={{ marginTop: 10 }}>
              <Select label="Sales Training Level" value={salesTraining} onChange={setSalesTraining} options={[
                { value: "none", label: "No formal training" },
                { value: "outdated", label: "Some — outdated" },
                { value: "recent", label: "Recent training" },
                { value: "ongoing", label: "Ongoing / structured" },
              ]} />
            </div>
          </Section>

          <Section step="4" title="Market & Position">
            <Select label="Market Type" value={marketType} onChange={setMarketType} options={[
              { value: "growing", label: "High-growth / new development" },
              { value: "suburban", label: "Established suburban" },
              { value: "urban", label: "Urban / city" },
              { value: "resort", label: "Resort / destination" },
              { value: "rural", label: "Rural / regional" },
              { value: "declining", label: "Declining / shrinking" },
            ]} />
            <div style={{ marginTop: 10 }}>
              <Select label="Pricing vs. Competitors" value={pricingPos} onChange={setPricingPos} options={[
                { value: "below", label: "Well below market" },
                { value: "slightly_below", label: "Slightly below market" },
                { value: "at", label: "At market rate" },
                { value: "slightly_above", label: "Slightly above market" },
                { value: "premium", label: "Premium / top of market" },
              ]} />
            </div>
            <div style={{ marginTop: 10 }}>
              <Row>
                <Field label="Competing Clubs (15 mi)" value={competitorCount} onChange={setCompetiorsCount} />
                <Select label="Digital Presence" value={digitalScore} onChange={v => setDigitalScore(v)} options={[
                  { value: "1", label: "1 — Very limited" },
                  { value: "2", label: "2 — Basic" },
                  { value: "3", label: "3 — Moderate" },
                  { value: "4", label: "4 — Strong" },
                  { value: "5", label: "5 — Excellent" },
                ]} />
              </Row>
            </div>
          </Section>

          <Section step="5" title="AshGro Services Selected">
            <div style={{ fontSize: 10, color: SLATE, marginBottom: 10, fontStyle: "italic" }}>Toggle services to see their impact on projections</div>
            {[
              { key: "ai", label: "AI Club Concierge", desc: "Leads +25%, Close +5%" },
              { key: "crm", label: "AshGro CRM Pro", desc: "Leads +10%, Close +8%, Retention +8%" },
              { key: "training", label: "Sales Training", desc: "Close +18%, Retention +5%" },
              { key: "remote", label: "Remote Sales Support", desc: "Leads +15%, Close +12%, Retention +6%" },
              { key: "staffing", label: "Onsite Staffing", desc: "Close +25%, Retention +5%" },
              { key: "consulting", label: "Membership & Club Consulting", desc: "Close +10%, All +6%" },
            ].map(({ key, label, desc }) => (
              <div key={key}
                onClick={() => toggleService(key)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 12px", borderRadius: 6, marginBottom: 6, cursor: "pointer",
                  background: services[key] ? NAVY : "#F0EEE9",
                  border: `1.5px solid ${services[key] ? GOLD : "#D5D0C8"}`,
                  transition: "all 0.15s"
                }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: services[key] ? GOLD : NAVY }}>{label}</div>
                  <div style={{ fontSize: 10, color: services[key] ? "#94A3B8" : SLATE }}>{desc}</div>
                </div>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%",
                  background: services[key] ? GOLD : "#D5D0C8",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, color: services[key] ? NAVY : "#999", fontWeight: 800
                }}>{services[key] ? "✓" : "+"}</div>
              </div>
            ))}
          </Section>
        </div>

        {/* RIGHT: Results */}
        <div style={{ padding: "20px 22px", overflowY: "auto" }}>

          {/* Current State Summary */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              📊 Current State — {clubName || "Club Name"} Baseline
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 10 }}>
              <MetricCard label="Capacity Used" value={`${calc.capacityPct}%`} sub={`${currentMembers} of ${capacity} slots`} />
              <MetricCard label="Annual Attrition" value={calc.annualAttrition} sub={`${attritionRate}% per year`} color={attritionRate > 10 ? RED : attritionRate > 7 ? AMBER : GREEN} />
              <MetricCard label="New Members/Yr" value={calc.currentNewMembers} sub="at current process" />
              <MetricCard label="Net Trend" value={(calc.floorNet >= 0 ? "+" : "") + calc.floorNet} sub="members per year" color={calc.floorNet >= 0 ? GREEN : RED} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              <MetricCard label="Annual Leads" value={calc.annualLeads} sub={`${monthlyLeads}/month`} />
              <MetricCard label="Annual Tours" value={Math.round(calc.toursPerYear)} sub={`${tourConvRate}% inquiry→tour`} />
              <MetricCard label="Open Slots" value={calc.openSlots} sub={`$${((calc.openSlots * calc.avgRevPerMember) / 1000).toFixed(0)}K opportunity`} color={GOLD} />
            </div>
          </div>

          {/* Insights */}
          {calc.insights.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>🔍 Key Findings</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {calc.insights.map((ins, i) => {
                  const c = insightColors[ins.type];
                  return (
                    <div key={i} style={{ background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 6, padding: "8px 12px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{c.icon}</span>
                      <span style={{ fontSize: 12, color: NAVY, lineHeight: 1.5 }}>{ins.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 12-Month Scenarios */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>📈 12-Month Membership Growth Scenarios</div>
            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <ScenarioCard label="Floor (Status Quo)" members={calc.floorTotal} delta={calc.floorNet} revenue={calc.floorRev} color={calc.floorNet < 0 ? RED : SLATE} bg="#F8F8F8" icon="📉" />
              <ScenarioCard label="Base Case (AshGro)" members={calc.baseTotal} delta={calc.baseNet} revenue={calc.baseRev} color={NAVY} bg={BEIGE} icon="📊" />
              <ScenarioCard label="Upside" members={calc.upsideTotal} delta={calc.upsideNet} revenue={calc.upsideRev} color={GREEN} bg="#F0FDF4" icon="🚀" />
            </div>
            <div style={{ fontSize: 10, color: SLATE, fontStyle: "italic", marginTop: -6 }}>
              * Base case assumes AshGro selected services above are implemented. Upside assumes optimal execution and market conditions. These are projections, not guarantees.
            </div>
          </div>

          {/* Conversion Funnel */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>🔄 Sales Funnel — Current vs. Improved</div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={calc.funnel} margin={{ top: 5, right: 10, left: 10, bottom: 5 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, fill: NAVY }} width={110} />
                <Tooltip content={<Tooltip2 />} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="current" name="Current" fill={SLATE} radius={[0, 3, 3, 0]} />
                <Bar dataKey="improved" name="With AshGro" fill={GOLD} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 12-Month Trajectory */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>📅 12-Month Membership Trajectory</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={calc.proj36} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} />
                <Tooltip content={<Tooltip2 />} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="floor" name="Floor" stroke={SLATE} strokeDasharray="4 4" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="base" name="Base Case" stroke={NAVY} dot={false} strokeWidth={2.5} />
                <Line type="monotone" dataKey="upside" name="Upside" stroke={GREEN} dot={false} strokeWidth={2} strokeDasharray="6 2" />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 10, color: SLATE, fontStyle: "italic", textAlign: "center", marginTop: 2 }}>Capacity ceiling: {capacity} members</div>
          </div>

          {/* Revenue Summary */}
          <div style={{ background: NAVY, borderRadius: 10, padding: "16px 20px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: GOLD, marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>💰 Estimated 12-Month Revenue Impact</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4 }}>Floor</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: calc.floorRev < 0 ? "#F87171" : "#94A3B8" }}>{calc.floorRev >= 0 ? "+" : ""}${Math.abs(calc.floorRev).toLocaleString()}</div>
                <div style={{ fontSize: 10, color: "#64748B" }}>new annual revenue</div>
              </div>
              <div style={{ textAlign: "center", borderLeft: `1px solid rgba(255,255,255,0.1)`, borderRight: `1px solid rgba(255,255,255,0.1)` }}>
                <div style={{ fontSize: 10, color: GOLD, textTransform: "uppercase", marginBottom: 4 }}>Base Case</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: GOLD }}>+${calc.baseRev.toLocaleString()}</div>
                <div style={{ fontSize: 10, color: "#94A3B8" }}>new annual revenue</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#86EFAC", textTransform: "uppercase", marginBottom: 4 }}>Upside</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#86EFAC" }}>+${calc.upsideRev.toLocaleString()}</div>
                <div style={{ fontSize: 10, color: "#64748B" }}>new annual revenue</div>
              </div>
            </div>
            <div style={{ marginTop: 12, padding: "10px 0 0", borderTop: "1px solid rgba(255,255,255,0.1)", fontSize: 10, color: "#64748B", fontStyle: "italic", textAlign: "center" }}>
              Revenue includes estimated new annual dues + F&B minimums + 15% initiation fee contribution. Excludes existing member revenue.
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
